import React, { useState, useEffect } from "react";
import axios from "axios";
import { Truck, CheckCircle2, Mail, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/carrier-vendors`;

// Data-driven KYC sections (core EU carrier fields; extend as needed).
const SECTIONS = [
  { id: "company", title: "Company & KYC", fields: [
    ["registered_name", "Registered company name", "text", true],
    ["trading_name", "Trading name", "text"],
    ["legal_form", "Legal form", "text"],
    ["registration_number", "Company registration number", "text"],
    ["vat_number", "VAT number", "text"],
    ["eori_number", "EORI number", "text"],
    ["country_of_registration", "Country of registration", "text"],
    ["registered_address", "Registered address", "text"],
    ["operating_address", "Operating address", "text"],
    ["website", "Website", "text"],
    ["main_telephone", "Main telephone", "text"],
    ["classification", "Classification (Carrier / Forwarder / Owner-driver / Fleet owner / Broker)", "text"],
    ["number_of_employees", "Number of employees", "number"],
  ]},
  { id: "contacts", title: "Contacts", fields: [
    ["admin_name", "Company administrator — name", "text"],
    ["admin_email", "Administrator email", "text"],
    ["ops_name", "Operations / dispatch contact", "text"],
    ["ops_email", "Operations email", "text"],
    ["ops_phone", "Operations phone", "text"],
    ["emergency_phone", "After-hours emergency phone", "text"],
    ["ar_email", "Accounts receivable email", "text"],
    ["ap_email", "Accounts payable email", "text"],
  ]},
  { id: "licences", title: "Transport Licences", list: true, fields: [
    ["licence_type", "Licence type (Community / National / Transport manager CPC)", "text"],
    ["licence_number", "Licence number", "text"],
    ["issuing_country", "Issuing country", "text"],
    ["issuing_authority", "Issuing authority", "text"],
    ["issue_date", "Issue date", "date"],
    ["expiry_date", "Expiry date", "date"],
    ["authorized_vehicles", "Authorized vehicle copies", "number"],
  ]},
  { id: "insurance", title: "Insurance", list: true, fields: [
    ["insurer", "Insurer", "text"],
    ["policy_number", "Policy number", "text"],
    ["insurance_type", "Type (CMR / Motor / Cargo / Liability)", "text"],
    ["territorial_coverage", "Territorial coverage", "text"],
    ["coverage_limit", "Coverage limit per incident", "number"],
    ["currency", "Currency", "text"],
    ["start_date", "Policy start date", "date"],
    ["expiry_date", "Expiry date", "date"],
  ]},
  { id: "fleet", title: "Fleet Profile (summary)", fields: [
    ["trucks_owned", "Trucks owned", "number"],
    ["trucks_leased", "Trucks leased", "number"],
    ["vans_owned", "Vans owned", "number"],
    ["trailers_owned", "Trailers owned", "number"],
    ["avg_vehicle_age", "Average vehicle age (yrs)", "number"],
    ["intl_vehicles", "Vehicles for international transport", "number"],
    ["gps_vehicles", "Vehicles with GPS tracking", "number"],
    ["vehicle_types", "Vehicle types (e.g. Tractor unit, Refrigerated, Box truck)", "text"],
  ]},
  { id: "routes", title: "Operating Regions & Routes", list: true, fields: [
    ["origin_country", "Origin country", "text"],
    ["origin_city", "Origin city / region", "text"],
    ["destination_country", "Destination country", "text"],
    ["destination_city", "Destination city / region", "text"],
    ["frequency", "Frequency per week", "text"],
    ["equipment", "Vehicle types used", "text"],
    ["capacity", "Monthly capacity", "text"],
    ["backhaul", "Backhaul available (yes/no)", "text"],
  ]},
  { id: "cargo", title: "Cargo Capabilities", fields: [
    ["accepted_cargo", "Accepted cargo (General, Food, Pharma, ADR, High-value...)", "text"],
    ["prohibited_cargo", "Prohibited cargo", "text"],
    ["adr_classes", "ADR classes accepted", "text"],
    ["temperature_ranges", "Temperature ranges", "text"],
    ["max_cargo_value", "Maximum cargo value", "number"],
  ]},
  { id: "compliance", title: "Compliance Questionnaire", fields: [
    ["sanctioned", "Company/director ever sanctioned? (explain if yes)", "text"],
    ["licence_suspended", "Transport licence ever suspended/withdrawn?", "text"],
    ["insolvency", "Any insolvency proceedings?", "text"],
    ["uses_subcontractors", "Uses subcontractors? (yes/no)", "text"],
    ["anti_bribery", "Anti-bribery controls in place? (yes/no)", "text"],
  ]},
  { id: "banking", title: "Banking & Tax", fields: [
    ["account_holder", "Account holder", "text"],
    ["bank_name", "Bank name", "text"],
    ["iban", "IBAN", "text"],
    ["swift", "SWIFT / BIC", "text"],
    ["account_currency", "Account currency", "text"],
    ["payment_terms", "Payment terms", "text"],
    ["invoice_email", "Invoice email", "text"],
  ]},
];

const Field = ({ f, value, onChange }) => {
  const [key, label, type, required] = f;
  return (
    <label className="block mb-3">
      <span className="text-xs font-medium text-slate-600">
        {label}{required ? <span className="text-red-500"> *</span> : <span className="text-slate-400"> (optional)</span>}
      </span>
      <input
        type={type}
        data-testid={`carrier-field-${key}`}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
        value={value ?? ""}
        onChange={(e) => onChange(key, type === "number" ? e.target.value : e.target.value)}
      />
    </label>
  );
};

const Wrap = ({ err, children }) => (
  <div className="min-h-screen bg-slate-50 py-10 px-4">
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-xl bg-orange-600 p-2 text-white"><Truck size={22} /></div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Carrier Registration</h1>
          <p className="text-sm text-slate-500">Europacific — EU road-freight carrier onboarding</p>
        </div>
      </div>
      {err && <div data-testid="carrier-error" className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{err}</div>}
      {children}
    </div>
  </div>
);

export default function CarrierRegistrationPage() {
  const [phase, setPhase] = useState("account"); // account | verify | wizard | done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cid, setCid] = useState(localStorage.getItem("carrier_id") || "");
  const [token, setToken] = useState(localStorage.getItem("carrier_token") || "");
  const [acct, setAcct] = useState({ email: "", first_name: "", last_name: "", company_name: "", vat_number: "", terms_accepted: false });
  const [code, setCode] = useState("");
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [decls, setDecls] = useState({ accuracy: false });
  const [result, setResult] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const [docs, setDocs] = useState([]);
  const [docUp, setDocUp] = useState({ type: "Transport licence", expiry: "" });

  useEffect(() => {
    // Resume from an emailed link (?cid=&token=) or from localStorage.
    const qs = new URLSearchParams(window.location.search);
    const qCid = qs.get("cid"); const qTok = qs.get("token");
    const rCid = qCid || cid; const rTok = qTok || token;
    if (qCid && qTok) {
      localStorage.setItem("carrier_id", qCid);
      localStorage.setItem("carrier_token", qTok);
      setCid(qCid); setToken(qTok);
    }
    if (rCid && rTok) {
      axios.get(`${API}/${rCid}?applicant_token=${rTok}`).then((r) => {
        const d = r.data || {};
        if (!d.email_verified) { setPhase("verify"); return; }
        const init = {};
        SECTIONS.forEach((s) => {
          init[s.id] = s.list
            ? (Array.isArray(d[s.id]) && d[s.id].length ? d[s.id] : [{}])
            : (d[s.id] || {});
        });
        setData(init); setPhase("wizard");
      }).catch(() => { localStorage.removeItem("carrier_id"); localStorage.removeItem("carrier_token"); });
    }
  }, []); // eslint-disable-line

  const start = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/register/start`, acct);
      setCid(r.data.id); setToken(r.data.applicant_token);
      localStorage.setItem("carrier_id", r.data.id);
      localStorage.setItem("carrier_token", r.data.applicant_token);
      if (r.data.vat_warning) setErr(r.data.vat_warning);
      setPhase("verify");
    } catch (e) { setErr(e.response?.data?.detail || "Could not start registration"); }
    setBusy(false);
  };

  const verify = async () => {
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/register/verify-email`, { id: cid, code });
      const init = {}; SECTIONS.forEach((s) => { init[s.id] = s.list ? [{}] : {}; });
      setData(init); setPhase("wizard");
    } catch (e) { setErr(e.response?.data?.detail || "Invalid code"); }
    setBusy(false);
  };

  const saveSection = async (sec) => {
    setBusy(true); setErr("");
    try {
      await axios.put(`${API}/${cid}`, { applicant_token: token, section: sec.id, data: data[sec.id] });
      if (step < SECTIONS.length - 1) setStep(step + 1); else setStep(SECTIONS.length); // to declarations
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await axios.put(`${API}/${cid}`, { applicant_token: token, section: "declarations", data: decls });
      const r = await axios.post(`${API}/${cid}/submit`, { applicant_token: token });
      setResult(r.data); setPhase("done");
    } catch (e) { setErr(e.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };

  const upd = (secId, list) => (key, val) => {
    setData((d) => {
      if (!list) return { ...d, [secId]: { ...(d[secId] || {}), [key]: val } };
      return d;
    });
  };
  const updList = (secId, idx) => (key, val) => {
    setData((d) => {
      const arr = [...(d[secId] || [{}])];
      arr[idx] = { ...(arr[idx] || {}), [key]: val };
      return { ...d, [secId]: arr };
    });
  };

  const saveAndExit = async () => {
    setBusy(true); setErr(""); setSavedMsg("");
    try {
      const curSec = SECTIONS[Math.min(step, SECTIONS.length - 1)];
      if (step < SECTIONS.length && curSec) {
        await axios.put(`${API}/${cid}`, { applicant_token: token, section: curSec.id, data: data[curSec.id] });
      }
      await axios.post(`${API}/${cid}/send-resume-link`, { applicant_token: token });
      setSavedMsg("Saved. We've emailed you a private link to finish later — you can safely close this page.");
    } catch (e) { setErr(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const uploadDoc = async (docType, expiry, fileObj) => {
    if (!fileObj) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("document_type", docType || "Document");
      if (expiry) fd.append("expiry_date", expiry);
      fd.append("applicant_token", token);
      const r = await axios.post(`${API}/${cid}/upload-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((prev) => [...prev, r.data.document]);
    } catch (e) { setErr(e.response?.data?.detail || "Upload failed (PDF/JPG/PNG, max 15MB)"); }
    setBusy(false);
  };


  const jump = async (target) => {
    // Free navigation: save current section best-effort, then move (no gating).
    const curOnDecl = step >= SECTIONS.length;
    const curSec = SECTIONS[Math.min(step, SECTIONS.length - 1)];
    if (!curOnDecl && curSec) {
      try { await axios.put(`${API}/${cid}`, { applicant_token: token, section: curSec.id, data: data[curSec.id] }); } catch (e) {}
    }
    setStep(target);
  };

  if (phase === "account") return (
    <Wrap err={err}>
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <Field f={["email", "Business email", "email", true]} value={acct.email} onChange={(k, v) => setAcct({ ...acct, email: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field f={["first_name", "First name", "text"]} value={acct.first_name} onChange={(k, v) => setAcct({ ...acct, first_name: v })} />
          <Field f={["last_name", "Last name", "text"]} value={acct.last_name} onChange={(k, v) => setAcct({ ...acct, last_name: v })} />
        </div>
        <Field f={["company_name", "Company name", "text", true]} value={acct.company_name} onChange={(k, v) => setAcct({ ...acct, company_name: v })} />
        <Field f={["vat_number", "VAT number", "text"]} value={acct.vat_number} onChange={(k, v) => setAcct({ ...acct, vat_number: v })} />
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" data-testid="carrier-terms" checked={acct.terms_accepted} onChange={(e) => setAcct({ ...acct, terms_accepted: e.target.checked })} />
          I accept the portal terms and privacy notice
        </label>
        <button data-testid="carrier-start-btn" disabled={busy || !acct.email || !acct.terms_accepted}
          onClick={start}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 py-2.5 font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />} Create account & send code
        </button>
      </div>
    </Wrap>
  );

  if (phase === "verify") return (
    <Wrap err={err}>
      <div className="rounded-2xl bg-white p-6 shadow-sm text-center">
        <ShieldCheck className="mx-auto mb-2 text-orange-600" size={28} />
        <p className="text-sm text-slate-600">We emailed a 6-digit code to <b>{acct.email || "your email"}</b>. Enter it to continue.</p>
        <input data-testid="carrier-code" value={code} onChange={(e) => setCode(e.target.value)}
          className="mx-auto mt-4 w-40 rounded-lg border border-slate-300 px-3 py-2 text-center text-lg tracking-widest" placeholder="123456" />
        <button data-testid="carrier-verify-btn" disabled={busy || code.length < 6} onClick={verify}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 py-2.5 font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Verify email"}
        </button>
      </div>
    </Wrap>
  );

  if (phase === "done") return (
    <Wrap err={err}>
      <div className="rounded-2xl bg-white p-6 shadow-sm text-center">
        <CheckCircle2 className="mx-auto mb-2 text-green-600" size={36} />
        <h2 className="text-lg font-bold text-slate-800">Application submitted</h2>
        <p className="mt-1 text-sm text-slate-600">Status: <b>{result?.status?.replace(/_/g, " ")}</b></p>
        {result?.checks && (
          <div className="mt-4 text-left">
            <p className="mb-2 text-xs font-semibold uppercase text-slate-400">Automatic checks</p>
            {result.checks.map((c, i) => (
              <div key={i} data-testid={`carrier-check-${c.check}`} className={`flex items-start gap-2 py-1 text-sm ${c.pass ? "text-slate-600" : "text-amber-700"}`}>
                <span>{c.pass ? "✅" : "⚠️"}</span><span>{c.check.replace(/_/g, " ")}{c.note ? ` — ${c.note}` : ""}</span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-xs text-slate-400">Our compliance team will review your application and may request more information by email.</p>
      </div>
    </Wrap>
  );

  // wizard
  const onDecl = step >= SECTIONS.length;
  const sec = SECTIONS[Math.min(step, SECTIONS.length - 1)];
  const sectionDone = (s) => {
    const v = data[s.id];
    if (!v) return false;
    if (s.list) return Array.isArray(v) && v.some((row) => row && Object.values(row).some((x) => x != null && String(x).trim() !== ""));
    return Object.values(v).some((x) => x != null && String(x).trim() !== "");
  };
  const doneCount = SECTIONS.filter(sectionDone).length;
  const pct = Math.round((doneCount / SECTIONS.length) * 100);
  return (
    <Wrap err={err}>
      <div className="mb-3" data-testid="carrier-progress">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="font-medium text-slate-600">{doneCount} of {SECTIONS.length} sections done</span>
          <span className="text-slate-400">{pct}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <p className="mb-2 text-xs text-slate-500">Only <b>Company name</b> is required — everything else is optional and you can jump between steps or finish later. Click any step below to navigate.</p>
      <div className="mb-3 flex flex-wrap gap-1">
        {SECTIONS.map((s, i) => (
          <button key={s.id} type="button" data-testid={`carrier-step-${s.id}`} onClick={() => jump(i)}
            className={`rounded-full px-2 py-0.5 text-[11px] ${i === step ? "bg-orange-600 text-white" : i < step ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>{s.title}</button>
        ))}
        <button type="button" data-testid="carrier-step-submit" onClick={() => jump(SECTIONS.length)}
          className={`rounded-full px-2 py-0.5 text-[11px] ${onDecl ? "bg-orange-600 text-white" : "bg-slate-200 text-slate-500"}`}>Submit</button>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <button type="button" data-testid="carrier-save-exit" disabled={busy} onClick={saveAndExit}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 disabled:opacity-50">
          💾 Save &amp; finish later
        </button>
      </div>
      {savedMsg && <div data-testid="carrier-saved-msg" className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700">{savedMsg}</div>}

      {!onDecl ? (
        <div className="rounded-2xl bg-white p-6 shadow-sm" data-testid={`carrier-section-${sec.id}`}>
          <h2 className="mb-4 text-lg font-bold text-slate-800">{sec.title}</h2>
          {sec.list ? (
            <>
              {((data[sec.id] && data[sec.id].length) ? data[sec.id] : [{}]).map((row, idx) => (
                <div key={idx} className="mb-4 rounded-xl border border-slate-200 p-3">
                  {sec.fields.map((f) => <Field key={f[0]} f={f} value={row[f[0]]} onChange={updList(sec.id, idx)} />)}
                </div>
              ))}
              <button data-testid="carrier-add-row" onClick={() => setData((d) => ({ ...d, [sec.id]: [...(d[sec.id] || [{}]), {}] }))}
                className="mb-2 text-sm font-medium text-orange-600">+ Add another</button>
            </>
          ) : (
            sec.fields.map((f) => <Field key={f[0]} f={f} value={(data[sec.id] || {})[f[0]]} onChange={upd(sec.id, false)} />)
          )}
          <div className="mt-4 flex justify-between">
            <button disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-40">Back</button>
            <button data-testid="carrier-next-btn" disabled={busy} onClick={() => saveSection(sec)} className="rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Saving..." : step < SECTIONS.length - 1 ? "Save & continue" : "Save & review"}
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-white p-6 shadow-sm" data-testid="carrier-declarations">
          <h2 className="mb-4 text-lg font-bold text-slate-800">Declarations & Submit</h2>

          <div className="mb-5 rounded-xl border border-slate-200 p-4" data-testid="carrier-documents-panel">
            <p className="text-sm font-semibold text-slate-700">Documents <span className="text-slate-400 font-normal">(optional — PDF/JPG/PNG)</span></p>
            <p className="mb-3 text-xs text-slate-500">Attach licences, insurance certificates or your registration extract. You can also add these later.</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-slate-600">Type
                <select data-testid="carrier-doc-type" value={docUp.type} onChange={(e) => setDocUp({ ...docUp, type: e.target.value })}
                  className="mt-1 block rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                  {["Transport licence", "CMR/Carrier insurance", "Vehicle insurance", "Company registration extract", "VAT certificate", "Other"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-600">Expiry
                <input type="date" data-testid="carrier-doc-expiry" value={docUp.expiry} onChange={(e) => setDocUp({ ...docUp, expiry: e.target.value })}
                  className="mt-1 block rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
              </label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="carrier-doc-file"
                onChange={(e) => { uploadDoc(docUp.type, docUp.expiry, e.target.files[0]); e.target.value = ""; }}
                className="text-xs" />
            </div>
            {docs.length > 0 && (
              <ul className="mt-3 space-y-1" data-testid="carrier-doc-list">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                    <span>📎 {d.document_type} — {d.original_filename}{d.expiry_date ? ` (exp ${d.expiry_date})` : ""}</span>
                    <span className="text-green-600">uploaded</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="carrier-accuracy" checked={decls.accuracy} onChange={(e) => setDecls({ ...decls, accuracy: e.target.checked })} />
            I confirm the information is complete and accurate, documents are genuine and current, material changes will be reported, and loads will not be subcontracted without permission.
          </label>
          <div className="mt-4 flex justify-between">
            <button onClick={() => setStep(SECTIONS.length - 1)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Back</button>
            <button data-testid="carrier-submit-btn" disabled={busy || !decls.accuracy} onClick={submit}
              className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Submitting..." : "Submit application"}
            </button>
          </div>
        </div>
      )}
    </Wrap>
  );
}
